
import { useLanguage, useNetwork } from '@/src/contexts';
import { getText, TranslationKey } from '@/src/helpers';
import {
  useExecuteXrplTransaction,
  useFireblockVaultAccountAssetAddressAtIndex,
  useRipplePathFind,
  useTokens,
} from '@/src/hooks';
import { TransactionType } from '@/src/models';
import { Button, Flex, Page, Spinner, Text } from '@/src/ui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightLeft } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { RipplePathFindResponse } from 'xrpl';
import {
  PathFindingError,
  PathFindingPending,
  PathFindingResults,
  TokenSelectors,
  tokenSelectorsFormSchema,
  TokenSelectorsFormValues,
} from './components';

export default function SwapPage() {
  const { language } = useLanguage();
  const { isTestnet } = useNetwork();

  // Use Fireblocks authentication instead of wallet connection
  const { vaultAccount, vaultAccountAssetAddress, isLoading } =
    useFireblockVaultAccountAssetAddressAtIndex();

  // Get XRPL address from Fireblocks
  const vaultXrplAddress = vaultAccountAssetAddress?.address;

  const tokens = useTokens();
  const { mutateAsync: executeXrplTransaction } = useExecuteXrplTransaction();
  const form = useForm<TokenSelectorsFormValues>({
    resolver: zodResolver(tokenSelectorsFormSchema),
    defaultValues: {
      from: {
        token: '',
        amount: '',
      },
      to: {
        token: '',
        amount: '-1',
      },
    },
    mode: 'onChange', // Validate on change for better UX
  });

  const [pathFindResult, setPathFindResult] = useState<RipplePathFindResponse['result']>();

  // 👇 mutation setup with retry
  const {
    mutateAsync: ripplePathFind,
    isPending: isFindingPath,
    isError,
    error,
    reset,
  } = useRipplePathFind({
    retry: false,
    onSuccess: (data) => {
      setPathFindResult(data);
    },
  });

  // 👇 watch form values
  const from = useWatch({ control: form.control, name: 'from' });
  const to = useWatch({ control: form.control, name: 'to' });

  // 👇 get tokens from the form
  const [toToken, fromToken] = useMemo(() => {
    const toToken = tokens.find((t) => t.id === to.token);
    const fromToken = tokens.find((t) => t.id === from.token);
    return [toToken, fromToken];
  }, [to.token, from.token, tokens]);

  // 👇 get network translation
  const getTranslation = (key: TranslationKey) => getText(language, key);

  // 👇 submit handler to execute the transaction on Fireblocks
  const onSubmit: SubmitHandler<TokenSelectorsFormValues> = async () => {
    if (!vaultAccount?.id || !pathFindResult) return;

    try {
      if (!vaultXrplAddress) {
        toast.error('Please configure your Fireblocks account to use the swap feature.');
        return;
      }

      if (!form.formState.isValid) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Get the best path from pathFindResult
      const bestPath = pathFindResult.alternatives?.[0];
      if (!bestPath) {
        toast.error('No valid paths found');
        return;
      }

      // Execute the transaction via Fireblocks
      await executeXrplTransaction({
        transactionType: TransactionType.CROSS_CURRENCY_PAYMENT,
        assetId: isTestnet ? 'XRP_TEST' : 'XRP',
        vaultAccountId: vaultAccount.id,
        params: {
          destination: vaultXrplAddress, // Self-payment for swap
          amount: to.amount,
          sendMax: from.amount, // Maximum amount you're willing to send
          paths: bestPath.paths_computed,
        },
      });

      toast.success('Swap executed successfully!');

      // Reset form after successful swap
      form.reset();
      setPathFindResult(undefined);
    } catch (error) {
      console.error('Swap execution error:', error);
      toast.error('Failed to execute swap. Please try again.');
    }
  };

  // 👇 auto-trigger path finding when form changes
  useEffect(() => {
    const isFormComplete =
      from.token !== '' && to.token !== '' && from.amount !== '' && to.amount !== '';

    const isSwappingSameToken = toToken?.code === fromToken?.code;

    // 👇 if we are swapping the same token, reset the form and path find result to avoid leaving a message in the UI
    if (isSwappingSameToken) {
      reset();
      setPathFindResult(undefined);
      return;
    }

    // 👇 only find path if we have a vault account asset address, the form is complete, and we are not swapping the same token
    const shouldFindPath =
      !!vaultXrplAddress && isFormComplete && !!fromToken && !isSwappingSameToken;

    if (!shouldFindPath) return;

    const timeout = setTimeout(async () => {
      await ripplePathFind({
        command: 'ripple_path_find',
        source_account: vaultXrplAddress,
        destination_account: vaultXrplAddress,
        destination_amount: to.amount,
        source_currencies: [
          {
            currency: fromToken.code,
            issuer: fromToken.code === 'XRP' ? undefined : fromToken.issuer,
          },
        ],
      });
    }, 250);

    // 👇 cleanup timeout - debounce
    return () => {
      clearTimeout(timeout);
    };
  }, [from, to, vaultXrplAddress, ripplePathFind, reset, fromToken, toToken]);

  let content: ReactNode = null;

  // Show loading state if API request is pending
  if (isLoading) {
    content = (
      <Flex align="center" justify="center" gap={2}>
        <Spinner />
        <Text className="text-muted-foreground">Loading Firebocks Vault Account...</Text>
      </Flex>
    );
  } else if (!vaultAccountAssetAddress) {
    content = (
      <Text className="text-muted-foreground text-center">
        Please configure your Fireblocks account to use the swap feature.
      </Text>
    );
  } else {
    content = (
      <div className="mx-auto max-w-md space-y-6 rounded-lg border p-6 bg-card">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <TokenSelectors />

            {isFindingPath && <PathFindingPending />}

            {isError && <PathFindingError error={error} />}

            {pathFindResult && (
              <PathFindingResults from={from} to={to} pathFindResult={pathFindResult} />
            )}

            <Button
              type="submit"
              disabled={!pathFindResult || pathFindResult?.alternatives?.length === 0}
              className="w-full"
              size="lg"
            >
              <ArrowRightLeft size={16} className="mr-2" />
              {getTranslation('swap')}
            </Button>
          </form>
        </FormProvider>

        {/* Additional Info */}
        <Text size="xs" className="text-muted-foreground text-center">
          This is a beta feature. Please review all details before confirming.
        </Text>
      </div>
    );
  }
  return (
    <Page>
      <Page.Body>{content}</Page.Body>
    </Page>
  );
}
